import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { CLIP_MARGIN_PX, clipBox, clippingAncestor, inlineNudge, overflowPast } from './clip';
import { cx } from './cx';
import { Spinner } from './Feedback';
import type { MenuItemModel, MenuModel } from './menuModel';
import styles from './menu.module.css';

/**
 * A trigger button and a popover of choices. Renders props (R13) — every label arrived formatted and
 * every `selected` was decided by a `model.ts`.
 *
 * It is the one primitive in `ui/` with behaviour rather than only appearance, and the behaviour is
 * the reason it exists: an outside click has to close it, Escape has to close it, and the item
 * semantics have to be right for a screen reader. Written once, it is right in both callers; written
 * per caller, it was right in the player and would have been re-derived here.
 *
 * `multiSelect` is not a cosmetic switch. `menuitemradio` announces "one of these" and
 * `menuitemcheckbox` announces "any of these", which is the difference between the sort menu and the
 * features menu, and it is the whole of what a reader has to know before pressing anything.
 */
export interface MenuProps {
  readonly menu: MenuModel;
  readonly open: boolean;
  /** `null` closes. The caller owns which menu is open, because at most one may be. */
  readonly onOpenChange: (open: boolean) => void;
  readonly onSelect: (itemId: string) => void;
  readonly multiSelect?: boolean;
  /**
   * `media` for the player's bar — a translucent control over video, where the page's button styling
   * has no contrast to work with (`media-*` roles exist because video is not a theme). `select`
   * dresses the trigger as the app's `Select` and matches the popover to its width — for a menu
   * sitting in a column of real selects (the settings page), where only the popover stays custom.
   * `pill` dresses it as the actions row's pills (the Watch on and Share menus) — the sm pill with
   * the Select's chevron on its right, and like the select face it prints no popover heading: the
   * trigger's own text already names the menu.
   */
  readonly variant?: 'default' | 'media' | 'select' | 'pill';
  /**
   * Stop Escape and outside-click from propagating past the popover. On for the player: its container
   * has its own key handler, and an Escape that reaches the browser in fullscreen is read as "leave
   * fullscreen" instead of "close this menu".
   */
  readonly containEvents?: boolean;
  /** A filter that cannot be used, with `title` saying why (§8's suppressed search controls). */
  readonly disabled?: boolean;
  readonly title?: string;
  /**
   * A glyph before the trigger's text — the player's four menus carry one so the bar reads as
   * controls rather than as a row of words. The icon is decoration: the accessible name is
   * unchanged, and what text accompanies it is the menu's own `valueShort` decision.
   */
  readonly triggerIcon?: ReactNode;
  /** Which edge the popover is anchored to and which way it opens. */
  readonly placement?: 'top-end' | 'bottom-start' | 'bottom-center' | 'top-center';
  /**
   * Whether the trigger shows the menu's name beside its value. On for a filter bar, where nothing
   * else says what a control is; **off** for the player, where the bar is tight enough that 1d
   * measured "1080p" as the only thing that fits — the name is still in the accessible label.
   */
  readonly showTriggerLabel?: boolean;
  /**
   * The trigger wears the chip's own "this is the answer to a question you asked" look (the
   * Watch on menu, once a fallback has replaced Visious's own player) — a state of the *world* the
   * control is reporting, not a value inside the menu, which is why it is its own prop rather than
   * routed through `menu.valueActive` (that one is for a value away from its default).
   */
  readonly triggerActive?: boolean;
  readonly className?: string;
  readonly triggerClassName?: string;
}

/**
 * The shortest a *capped* popover may be: about three rows and a heading. Below this, scrolling a panel
 * is worse than the clipping it avoids, so the cap declines and leaves the overflow for whoever can do
 * something better with it (see the fit effect).
 */
const MIN_FITTED_PX = 120;

export function Menu({
  menu,
  open,
  onOpenChange,
  onSelect,
  multiSelect = false,
  variant = 'default',
  containEvents = false,
  disabled = false,
  title,
  placement = 'bottom-start',
  showTriggerLabel = true,
  triggerActive = false,
  triggerIcon,
  className,
  triggerClassName,
}: MenuProps) {
  const root = useRef<HTMLDivElement | null>(null);
  const popover = useRef<HTMLDivElement | null>(null);

  const [flipped, setFlipped] = useState(false);
  // Only these two have a flip target: `top-end` already opens upward (the player's bar sits at the
  // bottom of the video, and flipping further is never the answer — see the fit effect below) and
  // nothing ever asks for `top-center` as an *un-flipped* starting placement.
  const flippable = placement === 'bottom-start' || placement === 'bottom-center';
  const effectivePlacement: 'top-end' | 'bottom-start' | 'bottom-center' | 'top-center' | 'top-start' =
    flippable && flipped ? (placement === 'bottom-start' ? 'top-start' : 'top-center') : placement;

  /**
   * Flip a downward popover upward when there is no room below it (2026-08-03) — a settings
   * dropdown near the foot of the page, or the Watch on pill once the failure box
   * centres them. Every caller with a `bottom-*` placement gets this for free now: it used to be
   * two copies of the same three lines, hand-written in `JumpOut` and `SwitchTo`, and neither
   * settings' plain filter-bar menus nor a `SelectMenu` ever had it at all.
   *
   * Measured against whatever clips the trigger (`clippingAncestor`) or, lacking one, the viewport
   * itself: a settings card has no clipping ancestor, and that is exactly the case this exists for
   * — the fixed bottom tab bar was covering a menu that had opened downward with nowhere to go.
   * Keyed on `open`/`placement` rather than `flipped`, so it measures once from the *un-flipped*
   * geometry per opening and never re-flips the flipped side.
   */
  useLayoutEffect(() => {
    if (!open || !flippable) return;
    const panel = popover.current;
    const anchor = root.current;
    if (panel === null || anchor === null) return;

    const edge = clipBox(anchor).bottom - CLIP_MARGIN_PX;
    if (panel.getBoundingClientRect().bottom > edge) setFlipped(true);

    return () => setFlipped(false);
  }, [open, placement, flippable]);

  /**
   * Fit the popover inside whatever clips it — one pass over three couplings, in the only
   * order that leaves all three measuring a panel that still exists afterwards.
   *
   * The stylesheet's `max-height: 62vh` is the *viewport's* answer, and on this app the thing that
   * usually clips a menu is not the viewport: the player sets `overflow: hidden`, so on a narrow window
   * — where the picture is a 16:9 box about 200 px tall and the bar sits at its bottom — a 22-language
   * audio menu opened upward was cut off at the top of the video, with no scrollbar and nothing on
   * screen to say rows were missing. Scrolling *inside* it reached them, which is precisely why the
   * defect reads as "the dropdowns are unusable" rather than as a clipped panel.
   *
   * So the cap becomes the room that actually exists between the trigger and that edge. Flipping to the
   * other side is **not** the answer here, tempting as it looks: the bar is at the bottom of the box, so
   * "below the trigger" is outside the same clip — and the popover cannot leave the player at all,
   * because `.player` *is* the fullscreen element and anything portalled out of it is not on screen in
   * fullscreen. A cap plus the scrolling the panel already does keeps every row reachable in both.
   *
   * A layout effect, before paint, so a too-tall panel is never painted at its natural height first.
   * Cleared on close so the next opening measures its own geometry rather than inheriting a cap from a
   * viewport that has since changed. Each `getBoundingClientRect` flushes the pending layout, which is
   * what lets the three steps below see each other's work inside one effect.
   */
  useLayoutEffect(() => {
    const panel = popover.current;
    const anchor = root.current;
    if (!open || panel === null || anchor === null) return;

    const box = clipBox(anchor);

    /*
     * 1. The inline cap, and it comes first because it is the one that changes how tall the panel is:
     * capped, its rows wrap. Only ever narrower than the room it has — at 360 px the quality menu is a
     * 365 px panel in a 345 px window, which is past the point where "shift it until it fits" has an
     * answer at all, and an uncapped one would be pinned to the start edge with its far side off
     * screen. Wider windows never reach this line.
     */
    const room = box.width - 2 * CLIP_MARGIN_PX;
    if (panel.getBoundingClientRect().width > room) {
      panel.style.maxWidth = `${String(Math.round(room))}px`;
    }

    /*
     * 2. The block cap. Measured from the panel **as laid out** rather than from the trigger, and that
     * is not a detail: the popover is anchored a `space-2` gap away from its trigger, so a cap computed
     * from the trigger's own box is that gap too generous and left 8 px of the intended 16 — measured
     * on a 420 px window, the panel's top landing 7 px below the player's edge with its shadow half
     * cut. Shrinking by the overflow instead needs no arithmetic about the anchor at all, works for
     * both directions, and does nothing when the panel already fits.
     *
     * Against `clippingAncestor` rather than `clipBox`, unlike the two steps around it: with nothing
     * clipping short of the viewport the stylesheet's `62vh` has already answered this question.
     */
    const clip = clippingAncestor(anchor);
    if (clip !== null) {
      const up =
        effectivePlacement === 'top-end' ||
        effectivePlacement === 'top-center' ||
        effectivePlacement === 'top-start';
      const overflow = overflowPast(panel.getBoundingClientRect(), clip, up ? 'up' : 'down');
      const fitted = panel.getBoundingClientRect().height - overflow;
      /*
       * A panel already inside its room needs no cap. And below `MIN_FITTED_PX` the cap **declines**,
       * which is the one line that keeps this from breaking the flip effect above: an answer of "no room
       * this way" that flips the other way would never see an overflow to react to if this effect ran
       * first and had already squeezed the panel into three scrolling pixels — both effects run against
       * the same un-flipped geometry on the opening render, since `flipped`'s own update lands one render
       * later. Refusing to produce a uselessly short panel leaves that overflow visible for the flip
       * effect to act on, and the flip then re-runs this one with the new `effectivePlacement` — where
       * there is room, and the cap applies.
       */
      if (overflow > 0 && fitted >= MIN_FITTED_PX) {
        panel.style.maxHeight = `${String(Math.round(fitted))}px`;
      }
    }

    /*
     * 3. The inline shift, and it comes **last** because step 2 moves the panel sideways: a capped
     * panel scrolls, a scrolling box takes a scrollbar, and a shrink-to-fit box grows by the
     * scrollbar's width rather than giving up content space for it. On an end-anchored player menu
     * that growth is all to the left — measured at 420 px, a panel nudged to sit 16 px inside the
     * frame ended up 8 px *outside* it once the height cap landed, and a shift computed before the cap
     * is a shift computed for a 15 px narrower panel.
     *
     * A shift rather than the flip step 2's neighbour uses: `.popoverMatch` grows past its trigger's
     * right edge for a long option, so a panel can be wider than the control it hangs off and
     * re-anchoring it to the other edge would only move the overflow. It is also the only answer the
     * centred placements can take at all — those are a `left: 50%` and a `translateX(-50%)`, and there
     * is no edge there to flip. Which is why it is written to `translate` and not to `transform`: the
     * individual transform property is applied *before* the shorthand rather than replacing it, so the
     * nudge adds to the centring instead of wiping it out.
     */
    const nudge = inlineNudge(panel.getBoundingClientRect(), box);
    if (nudge !== 0) panel.style.translate = `${String(Math.round(nudge))}px`;

    return () => {
      panel.style.maxWidth = '';
      panel.style.maxHeight = '';
      panel.style.translate = '';
    };
  }, [open, effectivePlacement]);

  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      if (containEvents) event.stopPropagation();
      onOpenChange(false);
    };

    // `pointerdown` rather than `click`: a click on a *scrollbar* never fires `click`, so a menu
    // closed on `click` stays open after someone scrolls the page behind it.
    const onPointerDown = (event: PointerEvent): void => {
      if (root.current?.contains(event.target as Node) === true) return;
      onOpenChange(false);
    };

    // Escape is listened for on the popover when contained (so it can be stopped before it travels)
    // and on the document otherwise (so it works with focus still on the trigger).
    const keyTarget: HTMLElement | Document = containEvents ? (popover.current ?? document) : document;
    keyTarget.addEventListener('keydown', onKey as EventListener);
    // Capture phase, deliberately: the close must see the pointerdown *before* anything can stop
    // it bubbling. The player's error overlay stops both gesture ends so a press on Try again is
    // not also a tap on the video — measured 2026-08-02: with a bubble listener, no press inside
    // the player ever reached the document, so the Watch on menu only closed from clicks outside.
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      keyTarget.removeEventListener('keydown', onKey as EventListener);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [containEvents, onOpenChange, open]);

  // An action menu (`actionMenu`) has no current setting: the trigger is the label alone, with no
  // colon and no muted styling — "Watch on" is the whole of what the control says.
  const accessibleName = menu.value === '' ? menu.label : `${menu.label}: ${menu.value}`;

  return (
    <div className={cx(styles.menu, className)} ref={root}>
      <Button
        variant={
          variant === 'media' ? 'media' : variant === 'pill' ? 'pill' : variant === 'select' ? 'default' : 'ghost'
        }
        // `default`/`md` is already the Select's box — 36 px, small type, bordered surface — so the
        // select face only adds the chevron and the width (`.triggerSelect`).
        size={variant === 'select' ? 'md' : 'sm'}
        className={cx(
          styles.trigger,
          variant === 'select' && styles.triggerSelect,
          variant === 'pill' && styles.triggerPill,
          triggerActive && styles.triggerActive,
          triggerClassName,
          open && styles.triggerOpen,
        )}
        ariaHasPopup="menu"
        ariaExpanded={open}
        // The accessible name carries the current value, so the control reads as
        // "Sort: Relevance" rather than as a button called "Relevance".
        label={accessibleName}
        disabled={disabled}
        // The accessible name doubles as the hover title, the same default `IconButton` has — the
        // player hides the label text, so hovering "1080p" is owed the word "Quality". An explicit
        // `title` (a disabled filter saying why) still wins.
        title={title ?? accessibleName}
        onClick={() => onOpenChange(!open)}
      >
        {triggerIcon ?? null}
        {showTriggerLabel ? (
          <span className={menu.value === '' ? undefined : styles.triggerLabel}>{menu.label}</span>
        ) : null}
        {/* `valueShort: null` is a real statement — icon alone, nothing to print (subtitles off). */}
        {menu.valueShort === null ? null : (
          <span className={cx(styles.triggerValue, menu.valueActive === true && styles.triggerValueActive)}>
            {menu.valueShort ?? menu.value}
          </span>
        )}
      </Button>

      {!open ? null : (
        <div
          className={cx(
            styles.popover,
            effectivePlacement === 'top-end' && styles.topEnd,
            effectivePlacement === 'bottom-start' && styles.bottomStart,
            effectivePlacement === 'bottom-center' && styles.bottomCenter,
            effectivePlacement === 'top-center' && styles.topCenter,
            effectivePlacement === 'top-start' && styles.topStart,
            variant === 'select' && styles.popoverMatch,
          )}
          role="menu"
          aria-label={menu.label}
          ref={popover}
        >
          {/* The select and pill faces print no heading — a native select puts no title over its
              options, and the control's own name (the row beside it, or the trigger's text) already
              says it. The name stays on the popover's `aria-label` and the trigger's accessible name. */}
          {variant === 'select' || variant === 'pill' ? null : (
            <p className={styles.menuHeading}>{menu.label}</p>
          )}
          {menu.sections.map((section, index) => (
            <div className={styles.section} key={section.title ?? `section-${index}`}>
              {section.title === null ? null : (
                <p className={styles.sectionTitle}>{section.title}</p>
              )}
              {section.items.map((item: MenuItemModel) =>
                item.href !== undefined ? (
                  // A link out of the app in menu clothes: a real `<a>` (middle-click, "copy link
                  // address" and a screen reader's link list keep working), `rel="noreferrer"` on
                  // purpose — telling YouTube which page the reader came from is most of what a
                  // privacy-preserving frontend exists to avoid, and the attribute holds for a
                  // same-tab navigation too. In the current tab (2026-08-02): following a
                  // jump-out means "keep watching over there", so the app yields the tab rather
                  // than leaving a copy of itself behind — unless the row asked for a new one
                  // (`newTab`, which is /embed's whole menu). `onSelect` still fires so the caller
                  // can close the menu.
                  <a
                    role="menuitem"
                    className={styles.item}
                    key={item.id}
                    href={item.href}
                    target={item.newTab === true ? '_blank' : undefined}
                    rel="noreferrer"
                    onClick={() => onSelect(item.id)}
                  >
                    <span className={styles.tick} aria-hidden="true" />
                    <span className={styles.label}>{item.label}</span>
                    {item.detail === null ? null : (
                      <span className={styles.detail}>{item.detail}</span>
                    )}
                  </a>
                ) : (
                  <button
                    type="button"
                    role={
                      item.action === true
                        ? 'menuitem'
                        : multiSelect || item.checkbox === true
                          ? 'menuitemcheckbox'
                          : 'menuitemradio'
                    }
                    // An action has no checked state to announce — see `MenuItemModel.action`.
                    aria-checked={item.action === true ? undefined : item.selected}
                    className={cx(styles.item, item.selected && styles.selected)}
                    key={item.id}
                    disabled={item.disabled === true || item.busy === true}
                    onClick={() => onSelect(item.id)}
                  >
                    <span className={styles.tick} aria-hidden="true">
                      {item.busy === true ? <Spinner small /> : item.selected ? '✓' : ''}
                    </span>
                    <span className={styles.label}>{item.label}</span>
                    {item.detail === null ? null : (
                      <span className={styles.detail}>{item.detail}</span>
                    )}
                  </button>
                ),
              )}
            </div>
          ))}
          {menu.note === null ? null : <p className={styles.note}>{menu.note}</p>}
        </div>
      )}
    </div>
  );
}
